'use client';

import React from 'react';
import { Keyboard } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import {
    getKeyboardShortcutDisplayValue,
    normalizeKeyboardShortcutToken,
    type KeyboardShortcutPlatformVariant,
} from './keyboardShortcutUtils';

const SEMANTIC_NAME = 'RomaKeyboardShortcut';
const SEMANTIC_NAME_GROUP = 'RomaKeyboardShortcutGroup';

export type KeyboardShortcutColor = 'primary' | 'secondary';

export type KeyboardShortcutProps = {
    children: string;
    preferSymbol?: boolean;
    platformVariant?: KeyboardShortcutPlatformVariant;
    color?: KeyboardShortcutColor;
    className?: string;
};

export type KeyboardShortcutPart =
    | string
    | { key: string; className?: string }
    | { text: React.ReactNode; className?: string };

export type KeyboardShortcutGroupProps = {
    parts: KeyboardShortcutPart[];
    preferSymbol?: boolean;
    platformVariant?: KeyboardShortcutPlatformVariant;
    color?: KeyboardShortcutColor;
    className?: string;
};

const styles = {
    color: {
        primary: 'text-primary',
        secondary: 'text-quaternary',
    },
    text: "whitespace-nowrap select-none text-xs font-normal leading-4 tracking-tight [font-feature-settings:'zero']",
} as const;

const KeyboardShortcutBase: React.FC<KeyboardShortcutProps> = ({
    children,
    preferSymbol = false,
    platformVariant = 'apple',
    color = 'secondary',
    className,
}) => {
    const normalizedToken = normalizeKeyboardShortcutToken(children);

    if (!normalizedToken) return null;

    const displayValue = getKeyboardShortcutDisplayValue({
        token: normalizedToken,
        preferSymbol,
        platformVariant,
    });

    return (
        <Keyboard
            className={cx(
                SEMANTIC_NAME,
                'inline-flex shrink-0 items-start',
                'rounded-xs bg-secondary-alpha px-1 py-px',
                styles.text,
                'capitalize',
                styles.color[color],
                className,
            )}
            title={displayValue !== normalizedToken ? normalizedToken : undefined}
        >
            {displayValue}
        </Keyboard>
    );
};

const isKeyPart = (part: KeyboardShortcutPart): part is string | { key: string; className?: string } =>
    typeof part === 'string' || 'key' in part;

const getKeyPartValue = (part: string | { key: string }): string => (typeof part === 'string' ? part : part.key);

const KeyboardShortcutGroup: React.FC<KeyboardShortcutGroupProps> = ({
    parts,
    preferSymbol = false,
    platformVariant = 'apple',
    color = 'secondary',
    className,
}) => {
    if (!parts.length) return null;

    return (
        <span className={cx(SEMANTIC_NAME_GROUP, 'inline-flex items-center gap-1', className)}>
            {parts.map((part, index) => {
                if (isKeyPart(part)) {
                    const key = getKeyPartValue(part);
                    const partClassName = typeof part === 'string' ? undefined : part.className;

                    return (
                        <KeyboardShortcutBase
                            key={`${platformVariant}-${preferSymbol ? 'symbol' : 'text'}-${key}-${index}`}
                            preferSymbol={preferSymbol}
                            platformVariant={platformVariant}
                            color={color}
                            className={cx(partClassName)}
                        >
                            {key}
                        </KeyboardShortcutBase>
                    );
                }

                return (
                    <span key={`text-${index}`} className={cx(styles.text, styles.color[color], part.className)}>
                        {part.text}
                    </span>
                );
            })}
        </span>
    );
};

KeyboardShortcutBase.displayName = SEMANTIC_NAME;
KeyboardShortcutGroup.displayName = SEMANTIC_NAME_GROUP;

export const KeyboardShortcut: React.FC<KeyboardShortcutProps> & {
    Group: React.FC<KeyboardShortcutGroupProps>;
} = Object.assign(KeyboardShortcutBase, {
    Group: KeyboardShortcutGroup,
});
